<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Los concesionarios de automóviles que publican tours virtuales en su
            web y en su perfil de Google Business generan
            <strong>un 40 % más de leads</strong> procedentes de búsquedas en
            línea. En una época en que los compradores investigan intensamente
            su vehículo en línea antes de visitar el concesionario, un showroom
            virtual 360° es un verdadero motor de negocio.
          </p>
        </section>

        <section>
          <h2>Por qué los tours virtuales funcionan para los concesionarios</h2>
          <p>
            La compra de un vehículo es una decisión de alta reflexión — los
            compradores pasan semanas comparando antes de visitar. Un tour
            virtual les permite «recorrer» tu showroom, explorar el área de
            servicio y previsualizar tu selección de vehículos desde su teléfono
            u ordenador. Esto genera familiaridad y confianza que convierten la
            visita física en una simple formalidad.
          </p>
        </section>

        <section>
          <h2>Qué incluir en tu tour virtual de concesionario</h2>
          <ul>
            <li>
              <strong>Showroom</strong> — Tomas amplias mostrando toda la gama
              de vehículos
            </li>
            <li>
              <strong>Vehículos individuales</strong> — Tours 360° de los
              modelos estrella (exterior e interior)
            </li>
            <li>
              <strong>Centro de servicio</strong> — Taller, sala de espera y
              mesa de asesores de servicio
            </li>
            <li>
              <strong>Oficina de financiación</strong> — Reducir la ansiedad
              ante los trámites administrativos
            </li>
            <li>
              <strong>Aparcamiento exterior</strong> — Mostrar el inventario
              completo y la facilidad de aparcamiento
            </li>
          </ul>
        </section>

        <section>
          <h2>Cómo crear tu tour virtual de concesionario</h2>
          <p>
            Usa la aplicación Travvir para capturar cada zona del concesionario.
            Para interiores de vehículos, abre todas las puertas y captura desde
            el asiento del conductor para máxima inmersión. Filma entre semana
            por la mañana, antes de que el aparcamiento se llene de clientes y
            empleados. Añade hotspots con el nombre de los modelos, precios y
            CTAs «Prueba disponible» vinculados a tu sistema de reservas.
          </p>
        </section>

        <section>
          <h2>Dónde usar tu tour virtual de concesionario</h2>
          <ul>
            <li>Insertado en tu página de inicio y fichas de vehículos</li>
            <li>Añadido a tu perfil de Google Business como foto 360°</li>
            <li>
              Usado en anuncios de Facebook e Instagram dirigidos a compradores
              en fase de investigación
            </li>
            <li>
              Incluido en seguimientos por e-mail a prospectos que visitaron tu
              web sin contactar
            </li>
          </ul>
        </section>

        <section>
          <h2>ROI de los tours virtuales para concesionarios</h2>
          <p>
            Los concesionarios que usan tours virtuales reportan: mayor tiempo
            en el sitio web (+3 minutos de media), más visitas recurrentes a las
            fichas de vehículos, mejor tasa de conversión de consulta a visita
            física, y menos «no-shows» en pruebas de conducción gracias a una
            mejor calificación previa.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/tour-virtual-negocio-local">Tour Virtual para Negocios Locales: Por Qué lo Necesitas en 2026</a></li>
          <li><a href="/blog/es/tour-virtual-google-mi-negocio">Tour Virtual en Google Business: Cómo Añadir Fotos 360° a tu Ficha</a></li>
          <li><a href="/blog/es/crear-tour-virtual-con-movil">Crear un Tour Virtual con el Móvil: Guía Completa 2026</a></li>
          <li><a href="/blog/es/generacion-leads-tour-virtual">Generación de Leads con Tours Virtuales: 5 Estrategias</a></li>
          <li><a href="/blog/es/fotos-360-seo-local">Fotos 360° y SEO Local: Mejora Tu Posicionamiento en Google</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-concesionario",
);

useBlogSeo({
  title: "Tour Virtual para Concesionarios de Coches: Guía Completa | Travvir",
  description:
    "Cómo crear un tour virtual para tu concesionario. Showroom, interiores de vehículos, área de servicio — guía de captura y estrategia de distribución para generar un 40 % más de leads.",
  keywords:
    "tour virtual concesionario, showroom virtual 360, tour virtual garage, visite virtuelle concession automóvil",
  slug: "tour-virtual-concesionario",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Cómo capturar el interior de un vehículo en 360°?",
      answer:
        "Abre todas las puertas y posiciónate desde el asiento del conductor para la toma más inmersiva.",
    },
    {
      question:
        "¿Hace falta un tour por vehículo o uno solo para el concesionario?",
      answer:
        "Idealmente ambos: un tour general del showroom y tours individuales para los modelos estrella o los vehículos de ocasión premium.",
    },
  ],
});

</script>
